import { useMemo, useState } from 'react';
import { Button, Field, Modal } from '../ui';
import { quizApi, apiErrorMessage } from '../../services/api';
import { useOrg } from '../../context/OrgContext';
import { useToast } from '../../context/ToastContext';
import { batchesForExam, defaultDurationMinutes } from '../../utils/batchExam';

export default function LaunchDialog({ exams, batches, onClose, onLaunched }) {
  const org = useOrg();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    examId: exams[0]?.examId || '',
    batchId: '',
    questionCount: 20,
    opensAt: '',
    closesAt: '',
    durationMinutes: '',
    allowedAttempts: 1
  });

  // A test must be on the exam its batch prepares for; the server rejects a
  // mismatch, so only compatible batches are offered.
  const eligibleBatches = useMemo(
    () => batchesForExam(batches, form.examId),
    [batches, form.examId]
  );

  const selectedBatchId = eligibleBatches.some((b) => b.id === form.batchId)
    ? form.batchId
    : (eligibleBatches[0]?.id || '');

  const suggestedDuration = defaultDurationMinutes(form.questionCount);

  const canLaunch = form.name.trim() && form.examId && selectedBatchId
    && Number(form.questionCount) > 0;

  const handleLaunch = async () => {
    setSaving(true);
    try {
      const payload = {
        examId: form.examId,
        name: form.name.trim(),
        batchId: selectedBatchId,
        questionCount: Number(form.questionCount),
        allowedAttempts: Number(form.allowedAttempts),
        ...(form.opensAt && { opensAt: new Date(form.opensAt).toISOString() }),
        ...(form.closesAt && { closesAt: new Date(form.closesAt).toISOString() }),
        ...(form.durationMinutes && { durationMinutes: Number(form.durationMinutes) })
      };
      const res = await quizApi.quickLaunch(org.id, payload);
      toast.success(`Quiz launched with ${res.data.questionCount} questions.`);
      onLaunched();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to launch quiz'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Quick launch" onClose={onClose}>
      <div className="form">
        <p className="helper">Prepgen.ai randomly selects questions from the chosen exam. Use the Screener when you need topic or difficulty control.</p>
        <Field label="Quiz name">
          <input value={form.name} placeholder="e.g. Weekly practice test" onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Exam">
          <select value={form.examId} onChange={(e) => setForm({ ...form, examId: e.target.value })}>
            {exams.map((x) => <option key={x.examId} value={x.examId}>{x.title}</option>)}
          </select>
        </Field>
        <Field label="Batch">
          <select value={selectedBatchId} onChange={(e) => setForm({ ...form, batchId: e.target.value })}>
            {eligibleBatches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
        {!eligibleBatches.length && (
          <p className="helper">No batch is preparing for this exam. Create one, or point an existing batch at it from the Batches page.</p>
        )}
        <Field label="Questions">
          <input type="number" min="1" max="200" value={form.questionCount} onChange={(e) => setForm({ ...form, questionCount: e.target.value })} />
        </Field>
        <Field label="Time limit in minutes (optional)">
          <input
            type="number"
            min="1"
            max="600"
            placeholder={`Defaults to ${suggestedDuration}`}
            value={form.durationMinutes}
            onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })}
          />
        </Field>
        <Field label="Opens (optional, your local time)">
          <input type="datetime-local" value={form.opensAt} onChange={(e) => setForm({ ...form, opensAt: e.target.value })} />
        </Field>
        <Field label="Closes (optional, your local time)">
          <input type="datetime-local" value={form.closesAt} onChange={(e) => setForm({ ...form, closesAt: e.target.value })} />
        </Field>
        <Field label="Allowed attempts">
          <select value={form.allowedAttempts} onChange={(e) => setForm({ ...form, allowedAttempts: e.target.value })}>
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={-1}>Unlimited</option>
          </select>
        </Field>
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!canLaunch || saving} onClick={handleLaunch}>
            {saving ? 'Launching…' : 'Launch randomized quiz'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
